import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Search,
  Clock,
  Users,
  BookOpen,
  Image as ImageIcon,
  ArrowUp,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import LayoutSwitcher, { LayoutType } from "@/components/layout/Training/LayoutSwitcher";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import {
  fetchFilteredTrainings,
  setTrainingFilter,
  resetTrainingFilter,
} from "@/features/training/trainingSlice";
import { Training, TrainingFilter } from "@/features/training/types";
import { useQuery } from "@apollo/client";
import { GET_TRAINING_IMAGE } from "@/lib/graphql/queries";

// TrainingImage Component
const TrainingImage = ({ filename, alt, className }: { filename: string; alt: string; className: string }) => {
  const { loading, error, data } = useQuery(GET_TRAINING_IMAGE, {
    variables: { filename },
    skip: !filename || filename.startsWith("http"),
  });

  if (loading) {
    return (
      <div className={`${className} overflow-hidden rounded-t-lg`}>
        <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
          <ImageIcon className="h-12 w-12 text-gray-400 opacity-50" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    console.error("Error loading image:", error?.message || "No data");
    return (
      <div className={`${className} overflow-hidden rounded-t-lg bg-gray-100 flex items-center justify-center`}>
        <div className="flex flex-col items-center justify-center">
          <ImageIcon className="h-10 w-10 text-gray-400 mb-2" aria-hidden="true" />
          <span className="text-xs text-gray-500">Image unavailable</span>
        </div>
      </div>
    );
  }

  if (filename.startsWith("http")) {
    return (
      <div className={`${className} overflow-hidden`}>
        <img
          src={filename}
          alt={alt}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          loading="lazy"
        />
      </div>
    );
  }

  if (data.getTrainingImageByName?.base64Data) {
    const { base64Data, mimetype } = data.getTrainingImageByName;
    return (
      <div className={`${className} overflow-hidden`}>
        <img
          src={`data:${mimetype};base64,${base64Data}`}
          alt={alt}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div className={`${className} overflow-hidden rounded-t-lg bg-gray-100 flex items-center justify-center`}>
      <div className="flex flex-col items-center justify-center">
        <ImageIcon className="h-10 w-10 text-gray-400 mb-2" aria-hidden="true" />
        <span className="text-xs text-gray-500">Image unavailable</span>
      </div>
    </div>
  );
};

// Main Trainings Component
const Trainings = () => {
  const dispatch = useDispatch<any>(); // Properly type this in a real app
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { trainingsList, paginationMeta, trainingsLoading, trainingsError, filter } = useSelector(
    (state: RootState) => state.training
  );

  // Local state with initial values synced from URL if present
  const [layout, setLayout] = useState<LayoutType>("grid");
  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get("search") || "");
  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get("category") || "");
  const [selectedLevel, setSelectedLevel] = useState<string>(searchParams.get("level") || "");
  const [selectedFormat, setSelectedFormat] = useState<string>(searchParams.get("format") || "");
  const [location, setLocation] = useState<string>(searchParams.get("location") || "");
  const [certificate, setCertificate] = useState<string>(searchParams.get("certificate") || "all");

  const containerClasses = {
    grid: "grid md:grid-cols-2 lg:grid-cols-3 gap-6",
    list: "space-y-6",
    gallery: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8",
  };

  // Sync filters with URL and fetch data on mount or filter change
  useEffect(() => {
    const urlFilter: Partial<TrainingFilter> = {
      search: searchTerm || null,
      category: selectedCategory === "ALL" ? null : selectedCategory.toLowerCase() || null,
      level: selectedLevel === "ALL" ? null : selectedLevel.toLowerCase() || null,
      format: selectedFormat === "ALL" ? null : selectedFormat.toLowerCase() || null,
      location: location === "ALL" ? null : location.toLowerCase() || null,
      certificate: certificate === "yes" ? true : certificate === "no" ? false : null,
      page: Number(searchParams.get("page")) || filter.page,
      limit: Number(searchParams.get("limit")) || filter.limit,
    };
    dispatch(setTrainingFilter(urlFilter));
    dispatch(fetchFilteredTrainings(urlFilter));

    // Update URL params but don't include this in the dependency array
    const newParams = {
      ...(searchTerm && { search: searchTerm }),
      ...(selectedCategory && { category: selectedCategory }),
      ...(selectedLevel && { level: selectedLevel }),
      ...(selectedFormat && { format: selectedFormat }),
      ...(location && { location }),
      certificate,
      page: String(urlFilter.page),
      limit: String(urlFilter.limit),
    };
    setSearchParams(newParams);

  }, [
    dispatch,
    searchTerm,
    selectedCategory,
    selectedLevel,
    selectedFormat,
    location,
    certificate,
    filter.page,
    filter.limit,
    searchParams,
    setSearchParams,

    // Remove searchParams and setSearchParams from dependencies
  ]);

  const handleCourseClick = (trainingId: number) => {
    navigate(`/training/${trainingId}`);
  };

  // Filter Handlers
  const handleSearch = () => {
    dispatch(setTrainingFilter({ search: searchTerm, page: 1 }));
  };

  const handleCategoryFilter = (category: string) => {
    setSelectedCategory(category);
  };

  const handleLevelFilter = (level: string) => {
    setSelectedLevel(level);
  };

  const handleFormatFilter = (format: string) => {
    setSelectedFormat(format);
    if (format.toLowerCase() === "online") {
      setLocation("");
    }
  };

  const handleCertificateFilter = (value: string) => {
    setCertificate(value);
  };

  const handleLocationFilter = (loc: string) => {
    setLocation(loc);
  };

  const handlePageChange = (page: number) => {
    const newFilter = { ...filter, page };
    dispatch(setTrainingFilter(newFilter));
    dispatch(fetchFilteredTrainings(newFilter));

    // Update URL params directly
    searchParams.set('page', String(page));
    setSearchParams(searchParams);
  };

  // Fix the handlePageChange function



  const resetFilters = () => {
    setSearchTerm("");
    setSelectedCategory("");
    setSelectedLevel("");
    setSelectedFormat("");
    setCertificate("all");
    setLocation("");
    dispatch(resetTrainingFilter());
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  // Render Training Card
  const renderTraining = (training: Training) => {
    switch (layout) {
      case "grid":
        return (
          <Card
            key={training.id}
            className="group flex flex-col overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer border-muted/40 hover:border-primary/30"
            onClick={() => handleCourseClick(training.id)}
          >
            <div className="relative overflow-hidden">
              <TrainingImage
                filename={training.image}
                alt={training.title}
                className="w-full h-48 object-cover rounded-t-lg"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute top-2 right-2 flex gap-2">
                <Badge
                  variant={training.format.toLowerCase() === "online" ? "default" : "secondary"}
                  className="bg-white/90 text-primary hover:bg-white"
                >
                  {training.format}
                </Badge>
              </div>
              <div className="absolute bottom-0 left-0 w-full p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300 bg-gradient-to-t from-black/80 to-transparent">
                <p className="text-white text-sm line-clamp-2">{training.description}</p>
              </div>
            </div>
            <CardHeader className="pb-2 pt-4">
              <CardTitle className="text-lg line-clamp-2 group-hover:text-primary transition-colors">{training.title}</CardTitle>
              <div className="flex items-center justify-between text-sm text-muted-foreground mt-1">
                <span className="font-medium">{training.provider}</span>
                <span className="font-bold text-primary">
                  {training.price ? `${training.price} TND` : 'Free'}
                </span>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-muted-foreground" aria-hidden="true" />
                  <span>{training.duration}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users size={14} className="text-muted-foreground" aria-hidden="true" />
                  <span>{training.enrollments || 0} Enrolled</span>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen size={14} className="text-muted-foreground" aria-hidden="true" />
                  <span className="capitalize">{training.level}</span>
                </div>
                {training.format.toLowerCase() !== "online" && training.location && (
                  <div className="flex items-center gap-2">
                    <Users size={14} className="text-muted-foreground" aria-hidden="true" />
                    <span className="capitalize">{training.location}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      case "list":
        return (
          <Card
            key={training.id}
            className="group overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer border-muted/40 hover:border-primary/30"
            onClick={() => handleCourseClick(training.id)}
          >
            <div className="flex flex-col md:flex-row">
              <div className="md:w-1/3 relative overflow-hidden">
                <TrainingImage
                  filename={training.image}
                  alt={training.title}
                  className="h-48 md:h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <Badge
                  variant={training.format.toLowerCase() === "online" ? "default" : "secondary"}
                  className="absolute top-2 right-2 bg-white/90 text-primary hover:bg-white"
                >
                  {training.format}
                </Badge>
                <div className="absolute bottom-0 left-0 right-0 p-2 flex justify-between items-center">
                  <Badge variant="outline" className="bg-black/50 text-white border-none">
                    <BookOpen className="h-3 w-3 mr-1" />
                    {training.level}
                  </Badge>
                  <Badge variant="outline" className="bg-black/50 text-white border-none">
                    <Clock className="h-3 w-3 mr-1" />
                    {training.duration}
                  </Badge>
                </div>
              </div>
              <div className="flex-1 p-6">
                <div className="flex justify-between items-start mb-2">
                  <CardTitle className="group-hover:text-primary transition-colors">{training.title}</CardTitle>
                  <span className="font-bold text-primary whitespace-nowrap ml-4">
                    {training.price ? `${training.price} TND` : 'Free'}
                  </span>
                </div>
                <div className="flex items-center mb-3">
                  <span className="text-sm font-medium">{training.provider}</span>
                  <span className="mx-2 text-muted-foreground">•</span>
                  <span className="text-sm text-muted-foreground flex items-center">
                    <Users size={14} className="mr-1" />
                    {training.enrollments || 0} Enrolled
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{training.description}</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="bg-primary/5">
                    {training.category || 'General'}
                  </Badge>
                </div>
              </div>
            </div>
          </Card>
        );
      case "gallery":
        return (
          <Card
            key={training.id}
            className="group cursor-pointer hover:shadow-xl transition-all duration-300 overflow-hidden border-0"
            onClick={() => handleCourseClick(training.id)}
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <TrainingImage
                filename={training.image}
                alt={training.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-80 group-hover:opacity-70 transition-opacity duration-300" />

              <div className="absolute top-3 left-3 flex gap-2">
                <Badge
                  variant="outline"
                  className="bg-white/90 text-primary border-0 font-medium"
                >
                  {training.category || 'General'}
                </Badge>
              </div>

              <div className="absolute top-3 right-3">
                <Badge
                  variant={training.format.toLowerCase() === "online" ? "default" : "secondary"}
                  className="bg-primary text-white border-0"
                >
                  {training.format}
                </Badge>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-white/90 flex items-center">
                    <Clock className="h-3 w-3 mr-1 text-white/70" />
                    {training.duration}
                  </span>
                  <span className="text-sm font-medium text-white/90 flex items-center">
                    <Users className="h-3 w-3 mr-1 text-white/70" />
                    {training.enrollments || 0} Enrolled
                  </span>
                </div>
                <h3 className="text-xl font-semibold mb-1 group-hover:text-primary-foreground transition-colors">{training.title}</h3>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-white/80">{training.provider}</span>
                  <span className="font-bold text-white">
                    {training.price ? `${training.price} TND` : 'Free'}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        );
      default:
        return null;
    }
  };

  // Back to top functionality
  const topRef = useRef<HTMLDivElement>(null);
  const scrollToTop = () => {
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Popular categories for quick filters
  const popularCategories = [
    { name: "Programming", icon: <BookOpen className="h-4 w-4" />, value: "programming" },
    { name: "Design", icon: <ImageIcon className="h-4 w-4" />, value: "design" },
    { name: "Business", icon: <Users className="h-4 w-4" />, value: "business" },
    { name: "Marketing", icon: <Clock className="h-4 w-4" />, value: "marketing" },
  ];

  // Show back to top button when scrolled down
  const [showBackToTop, setShowBackToTop] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50" ref={topRef}>
      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 p-3 rounded-full bg-primary text-white shadow-lg z-50 hover:bg-primary/90 transition-all"
          aria-label="Back to top"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 py-12 md:py-20">
          <div className="max-w-3xl mx-auto text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Discover Your Next Learning Journey</h1>
            <p className="text-lg md:text-xl text-blue-100 mb-8">Explore thousands of courses and trainings to boost your skills and career</p>

            <div className="relative max-w-2xl mx-auto">
              <div className="flex">
                <div className="relative flex-grow">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <Input
                    placeholder="Search for trainings, skills, or topics..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    className="pl-10 pr-4 py-6 rounded-l-lg rounded-r-none border-0 text-base w-full focus-visible:ring-blue-500 text-black"
                  />
                </div>
                <Button
                  onClick={handleSearch}
                  className="rounded-l-none rounded-r-lg bg-amber-500 hover:bg-amber-600 text-white px-6 py-6 text-base font-medium"
                >
                  Search
                </Button>
              </div>
            </div>
          </div>

          {/* Quick Category Filters */}
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {popularCategories.map((category) => (
              <Button
                key={category.value}
                variant={selectedCategory.toLowerCase() === category.value ? "secondary" : "outline"}
                className={`bg-white/10 hover:bg-white/20 text-white border-white/20 ${selectedCategory.toLowerCase() === category.value ? 'bg-white/30' : ''}`}
                onClick={() => handleCategoryFilter(category.value)}
              >
                <span className="flex items-center gap-2">
                  {category.icon}
                  {category.name}
                </span>
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">

      {/* Mobile Filter Section */}
      <div className="lg:hidden mb-6">
        <div className="bg-white p-4 rounded-lg shadow-md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Filters</h2>
            <Button onClick={resetFilters} variant="ghost" size="sm">
              Reset
            </Button>
          </div>
          <div className="mb-4">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                size={16}
                aria-hidden="true"
              />
              <Input
                placeholder="Search trainings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="pl-10 text-black"
                aria-label="Search trainings"
              />
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
            <div className="min-w-[140px]">
              <Select value={selectedCategory} onValueChange={handleCategoryFilter}>
                <SelectTrigger className="h-9" aria-label="Category filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="ALL">All Categories</SelectItem>
                  <SelectItem value="programming">Programming</SelectItem>
                  <SelectItem value="design">Design</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[140px]">
              <Select value={selectedLevel} onValueChange={handleLevelFilter}>
                <SelectTrigger className="h-9" aria-label="Level filter">
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="ALL">All Levels</SelectItem>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[140px]">
              <Select value={selectedFormat} onValueChange={handleFormatFilter}>
                <SelectTrigger className="h-9" aria-label="Format filter">
                  <SelectValue placeholder="Format" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="ALL">All Formats</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="in-person">In-Person</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[140px]">
              <Select value={certificate} onValueChange={handleCertificateFilter}>
                <SelectTrigger className="h-9" aria-label="Certificate filter">
                  <SelectValue placeholder="Certificate" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="all">All Certificates</SelectItem>
                  <SelectItem value="yes">With Certificate</SelectItem>
                  <SelectItem value="no">No Certificate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {selectedFormat.toLowerCase() !== "online" && (
              <div className="min-w-[140px]">
                <Select value={location} onValueChange={handleLocationFilter}>
                  <SelectTrigger className="h-9" aria-label="Location filter">
                    <SelectValue placeholder="Location" />
                  </SelectTrigger>
                  <SelectContent className="bg-white z-50">
                    <SelectItem value="ALL">All Locations</SelectItem>
                    <SelectItem value="tunis">Tunis</SelectItem>
                    <SelectItem value="sfax">Sfax</SelectItem>
                    <SelectItem value="sousse">Sousse</SelectItem>
                    <SelectItem value="kairouan">Kairouan</SelectItem>
                    <SelectItem value="bizerte">Bizerte</SelectItem>
                    <SelectItem value="gabes">Gabes</SelectItem>
                    <SelectItem value="ariana">Ariana</SelectItem>
                    <SelectItem value="gafsa">Gafsa</SelectItem>
                    <SelectItem value="new york">New York</SelectItem>
                    <SelectItem value="london">London</SelectItem>
                    <SelectItem value="paris">Paris</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <Button onClick={handleSearch} className="w-full mt-3">
            <Search size={16} className="mr-2" aria-hidden="true" />
            Apply Filters
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Desktop Filter Section */}
        <div className="hidden lg:block lg:w-1/4">
          <div className="bg-white p-4 rounded-lg shadow-md sticky top-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Filters</h2>
              <Button onClick={resetFilters} variant="ghost" size="sm">
                Reset
              </Button>
            </div>
            <div className="mb-4">
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  size={16}
                  aria-hidden="true"
                />
                <Input
                  placeholder="Search trainings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className="pl-10 text-black"
                  aria-label="Search trainings"
                />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" htmlFor="category-filter">
                Category
              </label>
              <Select value={selectedCategory} onValueChange={handleCategoryFilter}>
                <SelectTrigger id="category-filter" aria-label="Category filter">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="ALL">All Categories</SelectItem>
                  <SelectItem value="programming">Programming</SelectItem>
                  <SelectItem value="design">Design</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" htmlFor="level-filter">
                Level
              </label>
              <Select value={selectedLevel} onValueChange={handleLevelFilter}>
                <SelectTrigger id="level-filter" aria-label="Level filter">
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="ALL">All Levels</SelectItem>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" htmlFor="format-filter">
                Format
              </label>
              <Select value={selectedFormat} onValueChange={handleFormatFilter}>
                <SelectTrigger id="format-filter" aria-label="Format filter">
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="ALL">All Formats</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="in-person">In-Person</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" htmlFor="certificate-filter">
                Certificate
              </label>
              <Select value={certificate} onValueChange={handleCertificateFilter}>
                <SelectTrigger id="certificate-filter" aria-label="Certificate filter">
                  <SelectValue placeholder="Select certificate" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="all">All Certificates</SelectItem>
                  <SelectItem value="yes">With Certificate</SelectItem>
                  <SelectItem value="no">No Certificate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {selectedFormat.toLowerCase() !== "online" && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" htmlFor="location-filter">
                  Location
                </label>
                <Select value={location} onValueChange={handleLocationFilter}>
                  <SelectTrigger id="location-filter" aria-label="Location filter">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent className="bg-white z-50">
                    <SelectItem value="ALL">All Locations</SelectItem>
                    <SelectItem value="tunis">Tunis</SelectItem>
                    <SelectItem value="sfax">Sfax</SelectItem>
                    <SelectItem value="sousse">Sousse</SelectItem>
                    <SelectItem value="kairouan">Kairouan</SelectItem>
                    <SelectItem value="bizerte">Bizerte</SelectItem>
                    <SelectItem value="gabes">Gabes</SelectItem>
                    <SelectItem value="ariana">Ariana</SelectItem>
                    <SelectItem value="gafsa">Gafsa</SelectItem>
                    <SelectItem value="new york">New York</SelectItem>
                    <SelectItem value="london">London</SelectItem>
                    <SelectItem value="paris">Paris</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={handleSearch} className="w-full">
              <Search size={16} className="mr-2" aria-hidden="true" />
              Apply Filters
            </Button>
          </div>
        </div>

        {/* Training Cards Section */}
        <div className="lg:w-3/4">
          <div className="flex justify-between items-center mb-4">
            <p className="text-muted-foreground">
              Showing {trainingsList.length} of {paginationMeta?.totalItems || 0} trainings
            </p>
            <LayoutSwitcher currentLayout={layout} onLayoutChange={setLayout} />
          </div>

          {trainingsLoading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" aria-label="Loading trainings"></div>
            </div>
          )}

          {trainingsError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              <p>
                Unable to load trainings. Please try again later or{" "}
                <button onClick={resetFilters} className="underline">
                  reset filters
                </button>
                .
              </p>
            </div>
          )}

          {!trainingsLoading && trainingsList.length === 0 && (
            <div className="text-center py-12 border rounded-lg">
              <h3 className="text-lg font-medium mb-2">No trainings found</h3>
              <p className="text-muted-foreground mb-4">Try adjusting your filters or resetting them.</p>
              <Button onClick={resetFilters} variant="outline">
                Reset Filters
              </Button>
            </div>
          )}

          {!trainingsLoading && trainingsList.length > 0 && (
            <div className={cn("transition-all duration-300", containerClasses[layout])}>
              {trainingsList.map(renderTraining)}
            </div>
          )}

          {paginationMeta && trainingsList.length > 0 && (
            <div className="mt-6">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => handlePageChange(Math.max(1, paginationMeta.currentPage - 1))}
                      className={
                        paginationMeta.currentPage <= 1
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                      aria-disabled={paginationMeta.currentPage <= 1}
                    />
                  </PaginationItem>
                  {(() => {
                    const totalPages = paginationMeta.totalPages || 1;
                    const currentPage = paginationMeta.currentPage || 1;
                    let startPage = Math.max(1, currentPage - 2);
                    let endPage = Math.min(totalPages, startPage + 4);
                    if (endPage - startPage < 4) {
                      startPage = Math.max(1, endPage - 4);
                    }
                    return Array.from(
                      { length: endPage - startPage + 1 },
                      (_, i) => startPage + i
                    ).map((page) => (
                      <PaginationItem key={page}>
                        <PaginationLink
                          isActive={page === currentPage}
                          onClick={() => handlePageChange(page)}
                          className="cursor-pointer"
                          aria-label={`Go to page ${page}`}
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ));
                  })()}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() =>
                        paginationMeta.currentPage < (paginationMeta.totalPages || 1) &&
                        handlePageChange(paginationMeta.currentPage + 1)
                      }
                      className={
                        paginationMeta.currentPage >= (paginationMeta.totalPages || 1)
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                      aria-disabled={paginationMeta.currentPage >= (paginationMeta.totalPages || 1)}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
              <div className="flex justify-end mt-4">
                <Select
                  value={String(filter.limit || 10)}
                  onValueChange={(value) => {
                    const limit = parseInt(value);
                    dispatch(setTrainingFilter({ limit, page: 1 }));
                  }}
                >
                  <SelectTrigger className="w-[180px]" aria-label="Items per page">
                    <SelectValue placeholder="Items per page" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 per page</SelectItem>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </div>

  );
};

export default Trainings;